/**
 * The Summary table, in the same shape it takes on the invoice.
 *
 * Grouped by client *and* rate, so a client billed at two rates is two rows and
 * a client with both billable and joint-venture work is two rows. Folding those
 * together would produce a single "Rate" cell that cannot be filled in
 * truthfully, which is why the invoice does not do it either.
 *
 * A joint-venture row shows its hours and nothing in the Subtotal column. Its
 * hours still count toward the hour total; its money does not exist. That is
 * the reason the footer's two numbers cannot be derived from one another.
 */

import type { SummaryRow } from '@/api/types'
import { formatMoney, rateLabel, trimHours } from '@/lib/consulting'

interface SummaryProps {
  rows: SummaryRow[]
  totalHours: number
  totalAmount: number
  /** What the numbers are counting, e.g. "3 selected rows". */
  caption: string
}

export function ConsultingSummary({ rows, totalHours, totalAmount, caption }: SummaryProps) {
  if (rows.length === 0) return null

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-line px-3 py-2.5">
        <h2 className="text-sm font-semibold">Summary</h2>
        <span className="text-xs text-ink-3">{caption}</span>
      </div>

      {/* Its own scroller: four columns of money do not fit at 320px, and the
          page body must never scroll sideways. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-md border-collapse text-sm">
          <thead>
            <tr className="bg-surface-3 text-left text-xs uppercase tracking-wide text-ink-3">
              <th scope="col" className="px-3 py-2 font-semibold">
                Client
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Hours
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Rate
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Subtotal
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.client_name}-${row.rate_kind}-${row.rate ?? 'jv'}`}
                className="border-t border-line"
              >
                <td className="px-3 py-1.5">{row.client_name}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{trimHours(row.hours)}</td>
                <td className="px-3 py-1.5 text-right">
                  {row.rate_kind === 'joint_venture' ? (
                    <span className="pill bg-info-light text-info-text">Joint Venture</span>
                  ) : (
                    rateLabel(row.rate_kind, row.rate)
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {row.amount === null ? '' : formatMoney(row.amount)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-line-strong font-bold">
              <td className="px-3 py-2">Total</td>
              <td className="px-3 py-2 text-right tabular-nums">{trimHours(totalHours)}</td>
              <td className="px-3 py-2" />
              <td className="px-3 py-2 text-right tabular-nums">{formatMoney(totalAmount)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  )
}
